import { useEffect, useRef, useState } from "react";

const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = ["mousemove", "mousedown", "keydown", "scroll", "wheel", "touchstart"];

/**
 * Tracks user inactivity.
 *
 * Returns `true` once there has been no user activity (mouse / keyboard / scroll /
 * touch) for `timeout` ms, or while the tab is hidden. Any activity — or the tab
 * becoming visible again — resets the state back to active.
 */
export const useIdle = (timeout: number): boolean => {
  const [isIdle, setIsIdle] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const startTimer = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setIsIdle(true), timeout);
    };

    const handleActivity = () => {
      setIsIdle(false);
      startTimer();
    };

    const handleVisibility = () => {
      if (document.hidden) {
        if (timerRef.current) clearTimeout(timerRef.current);
        setIsIdle(true);
      } else {
        handleActivity();
      }
    };

    ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, handleActivity, { passive: true }));
    document.addEventListener("visibilitychange", handleVisibility);

    startTimer();

    return () => {
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, handleActivity));
      document.removeEventListener("visibilitychange", handleVisibility);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [timeout]);

  return isIdle;
};
